import React from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConfiguratorResultType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FlowchartEditorLoadError,
  FlowchartTaskOrSchemaInaccessibleError,
} from 'processes/modals/ProcessFlowchartModalShared';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {useAllSchemasByIdWithSingletonSchemas} from '../useAllSchemasById';
import {useExecutionEditorConfigurator} from './executionFlowchartConfigurator';
import {FlowchartDataResultType, useFlowchartConfigData} from './executionFlowchartData';
import ExecutionFlowchartEditor from './ExecutionFlowchartEditor';
import {ACMExecutionFlowchartPaneProps} from './ExecutionFlowchartPaneTypes';

export default function ACMExecutionFlowchartPane(props: ACMExecutionFlowchartPaneProps): JSX.Element {
  const {configuratorResult, executionEditorConfigurator} = useExecutionEditorConfigurator(props.editorMode);
  const dataResult = useFlowchartConfigData(props.flowchartConfigId, executionEditorConfigurator, null);
  const data = dataResult.resultType === FlowchartDataResultType.SUCCESS ? dataResult.data : null;

  const needsSidepanel =
    executionEditorConfigurator?.hasFeature(ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL) ??
    false;

  const {
    schemasById: allSchemasById,
    processTaskDefinitionsResult: taskDefinitionsResult,
    singletonTaskSchemasByName,
  } = useAllSchemasByIdWithSingletonSchemas({
    flowchartEditorConfigurator: executionEditorConfigurator,
    rootTaskSchema: data?.rootTaskSchema ?? null,
    skipTaskDefinitionsQuery: !needsSidepanel,
    availableTaskSchemas: data?.availableTaskSchemas,
  });

  if (
    configuratorResult.resultType === ConfiguratorResultType.ERROR ||
    dataResult.resultType === FlowchartDataResultType.ERROR
  ) {
    return <FlowchartEditorLoadError />;
  }

  if (dataResult.resultType === FlowchartDataResultType.INACCESSIBLE) {
    return <FlowchartTaskOrSchemaInaccessibleError />;
  }

  if (executionEditorConfigurator == null || data == null || allSchemasById == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <ExecutionFlowchartEditor
      editorMode={props.editorMode}
      data={data}
      executionEditorConfigurator={executionEditorConfigurator}
      draftStateRef={props.draftStateRef}
      onBlockingErrorsChange={props.onBlockingErrorsChange}
      allSchemasById={allSchemasById}
      taskDefinitionsResult={taskDefinitionsResult}
      singletonTaskSchemasByName={singletonTaskSchemasByName}
    />
  );
}
